import ReactDOM from 'react-dom';
import { DisaggregatedVersion, MetricMetadataModalProps } from '../services/types';
import RaceEthnicityMetadataChart from './charts/RaceEthnicityMetadataChart';
import { capitalizeFirstLetter, formatValue } from '../utils/helpers';
import { useEffect, useRef, useState } from 'react';
import MetadataSkeleton from './skeletons/MetadataSkeleton';
import { sendGoogleAnalyticsEvent } from '../utils/messages';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
].join(',');

const MetricMetadataModal: React.FC<MetricMetadataModalProps> = ({
    isOpen,
    onClose,
    metadata,
    disaggData,
    stateData,
    nationalData,
    loading,
    error,
    triggerRef,
    modalId = 'metric-metadata-modal'
}) => {
    // live region announcement for SRs
    const [announcement, setAnnouncement] = useState('');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const headingRef = useRef<HTMLHeadingElement | null>(null); // initial focus target
    const descriptionRef = useRef<HTMLParagraphElement | null>(null);
    const wasOpenRef = useRef(false);

    // GA4 tracking for metadata panel views
    useEffect(() => {
        const metricName = metadata?.[0]?.metric_name;

        if (!isOpen || !metricName) return;

        sendGoogleAnalyticsEvent(
            "user_click",
            "metric_view",
            metricName
        );
    }, [isOpen, metadata?.[0]?.metric_name]);

    // Hide main scrollbar & key handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            } else if (e.key === 'Tab' && containerRef.current) {
                // trap focus within modal
                const focusables = Array.from(containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
                .filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);
                if (focusables.length === 0) {
                e.preventDefault();
                return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement as HTMLElement | null;

                if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
                } else if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = 'hidden';
            // SR announcement + set focus to heading on open
            setAnnouncement('Metric details dialog opened. Press Escape to close.');
            // Delay focus slightly to ensure nodes mounted
            setTimeout(() => {
                headingRef.current?.focus();
            }, 0);
            } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    // Return focus to trigger when closing
    useEffect(() => {
        const wasOpen = wasOpenRef.current;

        // Only return focus if the modal was open and is now closed.
        if (wasOpen && !isOpen && triggerRef?.current) {
            // Defer to next tick to ensure layout is settled
            setTimeout(() => triggerRef.current?.focus(), 0);
        }

        wasOpenRef.current = isOpen;
    }, [isOpen, triggerRef]);

    if (!isOpen) return null;

    console.log('metadata:', metadata);
    const currMetricId = metadata && metadata[0].id === 1116 ? metadata[0].id : 1078;
    // Set values for comparison details at state and national level
    const stateVersion = stateData ? stateData[0].versions[0] : null;
    const stateVal = stateVersion ? formatValue(stateVersion.value, stateVersion.unit, currMetricId) : '-';

    const nationalVersion = nationalData ? nationalData[0].versions[0] : null;
    const nationalVal = nationalVersion ? formatValue(nationalVersion.value, nationalVersion.unit, currMetricId) : '-';

    const requiredDisaggregations = [
        "White",
        "Black or African American",
        "American Indian and Alaska Native",
        "Asian",
        "Native Hawaiian and Other Pacific Islander",
        "Some Other Race",
        "Two or More Races"
    ];

    const calculateIsPercentOfTotal = (version: DisaggregatedVersion) => {
        const topLevelDenominator = version?.percent_calc?.d;
        const allDisaggregations = version?.disaggregations;
        if (!allDisaggregations) return false;
        const hasMissingPercentCalc = allDisaggregations.some(d => !d.percent_calc);
        const denominatorsMatch = allDisaggregations.every(
        d => d.percent_calc?.d === topLevelDenominator
        );
        return hasMissingPercentCalc || denominatorsMatch;
    };

    return ReactDOM.createPortal(
        <>
        {/* aria-live region: hidden visually, announced by SRs when opened */}
        <div aria-live="polite" role="status" className="sr-only">{announcement}</div>

        {/* Overlay */}
        <div
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            // Click outside closes, but keeps focus return behavior
            onMouseDown={(e) => {
            // Only close if the click started on the overlay, not inside the panel
            if (e.target === e.currentTarget) onClose();
            }}
        />

        {/* Dialog */}
        <div
            id={modalId}
            ref={containerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${modalId}-title`}
            aria-describedby={`${modalId}-desc`}
            className="fixed inset-0 z-[51] flex items-center justify-center"
        >
            <div className="bg-white p-6 w-full overflow-y-auto h-full relative transition-transform duration-500 transform">
            <button
                className="absolute top-3 right-5 text-gray-600 text-3xl font-bold hover:text-black"
                onClick={onClose}
                aria-label="Close modal"
            >
                &times;
            </button>

            {/* Title (initial focus target) */}
            <h3
                id={`${modalId}-title`}
                ref={headingRef}
                tabIndex={-1}
                className="text-color-cai-brown text-lg font-bold focus:outline-none"
            >
                {/* If we have metadata, use the first metric's name; fallback text otherwise */}
                {metadata?.[0]?.metric_name ?? 'Metric details'}
            </h3>

            {/* Short description for SR context */}
            <p id={`${modalId}-desc`} ref={descriptionRef} className="sr-only">
                This panel contains details, sources, race &amp; ethnicity breakdowns, and comparison values. Press Escape to close.
            </p>

            {loading ? (
                <MetadataSkeleton />
            ) : error ? (
                <p className="text-red-500 text-sm">Error fetching metric metadata.</p>
            ) : metadata && metadata.length > 0 ? (
                <div className="mt-2">
                {metadata.map((metric) => {
                    let selectedVal: string | number = 0;
                    const selectedMetric = disaggData?.find(d => d.id === metric.id) || null;
                    const version = selectedMetric?.versions[0];
                    const isPercentOfTotal = version && calculateIsPercentOfTotal(version);
                    if (version) selectedVal = formatValue(version.value, version.unit, currMetricId);

                    return (
                    <div key={metric.id} className="mb-4">
                        <p className='text-color-cai-brown text-xs w-11/12'>{metric.definition}</p>

                        {metric.versions && metric.versions.length > 0 && (
                        <div>
                            {metric.versions.map((version) => {
                            const hasSufficientDisaggregationsWithData =
                                disaggData?.some((data) =>
                                data.id === metric.id &&
                                requiredDisaggregations.filter((name) =>
                                    data.versions?.[0]?.disaggregations?.some(
                                    (d) => d.name === name && (d.percent_calc?.d ?? 0) > 0
                                    )
                                ).length >= 3
                                ) ?? false;

                            return (
                                <div key={version.metric_version_id_ext} className="my-2">
                                <h5 className='text-color-cai-brown font-bold text-sm mt-3'>Why Is This Metric Important?</h5>
                                <p className='text-color-cai-brown text-xs w-11/12'>{version.why_important}</p>

                                {/* Race & Ethnicity */}
                                {hasSufficientDisaggregationsWithData && metric.id !== 1088 ? (
                                    <>
                                    <h5 className='text-color-cai-brown font-bold text-sm mt-3'>Race & Ethnicity</h5>
                                    <p className='text-color-cai-brown text-xs w-11/12'>
                                        {isPercentOfTotal
                                        ? 'Each bar shows the specific racial/ ethnic group calculated against the total.'
                                        : 'Each bar shows the indicator calculated for that specific racial/ethnic group.'}
                                    </p>
                                    <div className='w-full'>
                                        <RaceEthnicityMetadataChart
                                        indicators={disaggData}
                                        title={metric.metric_name}
                                        metricId={metric.id}
                                        height='h-60'
                                        barColor='#5e4f4a'
                                        reverseOrder={false}
                                        sort="alphabetical"
                                        />
                                    </div>
                                    </>
                                ) : (
                                    <>
                                    <h5 className='text-color-cai-brown font-bold text-sm mt-3'>Race & Ethnicity</h5>
                                    <p className='text-color-cai-brown text-xs italic'>
                                        Race & ethnicity breakdown not available for this indicator.
                                    </p>
                                    </>
                                )}

                                {/* Comparison table */}
                                {(version.unit === 'percentage' || version.unit === 'ratio') && (
                                    <>
                                    <h5 className='text-color-cai-brown font-bold text-sm mt-3'>Comparison Details</h5>
                                    <div className='flex justify-center mt-3'>
                                        <div className="overflow-hidden rounded-lg border border-color-cai-lightGray border-opacity-80 w-4/5">
                                        <table className="table-auto border-collapse w-full">
                                            <thead>
                                            <tr>
                                                <th className="border-b border-color-cai-lightGray border-opacity-80"></th>
                                                <th className="border-l border-color-cai-lightGray border-opacity-80 px-2 py-1 text-center text-xs text-color-cai-brown">{metric.metric_name}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr>
                                                <td className="border-b border-color-cai-lightGray border-opacity-80 px-2 py-1 text-xs text-color-cai-brown">State</td>
                                                <td className="border-t border-l border-color-cai-lightGray border-opacity-80 px-2 py-1 text-xs text-color-cai-brown font-bold text-center">{stateVal}</td>
                                            </tr>
                                            <tr>
                                                <td className="px-2 py-1 text-xs text-color-cai-brown">Nation</td>
                                                <td className="border-t border-l border-color-cai-lightGray border-opacity-80 px-2 py-1 text-xs text-color-cai-brown font-bold text-center">{nationalVal}</td>
                                            </tr>
                                            <tr>
                                                <td className="border-t border-r border-color-cai-lightGray border-opacity-80 px-2 py-1 text-xs text-color-cai-brown">Selected Area</td>
                                                <td className="border-t border-color-cai-lightGray border-opacity-80 py-1 text-xs text-color-cai-brown font-bold text-center">{selectedVal}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        </div>
                                    </div>
                                    </>
                                )}

                                {/* Details */}
                                <dl className="grid grid-cols-2 gap-4 mt-6">
                                    <div className="flex flex-col">
                                    <dt className="text-color-cai-brown font-bold text-sm">Data Year</dt>
                                    <dd className="text-color-cai-brown text-xs">{version.year}</dd>
                                    </div>
                                    <div className="flex flex-col">
                                    <dt className="text-color-cai-brown font-bold text-sm">Scope</dt>
                                    <dd className="text-color-cai-brown text-xs">{capitalizeFirstLetter(version.level_of_detail)}</dd>
                                    </div>
                                    <div className="col-span-2">
                                    <dt className="text-color-cai-brown font-bold text-sm">Source</dt>
                                    <dd className="text-color-cai-brown text-xs w-11/12">{version.source}</dd>
                                    </div>
                                </dl>

                                {version.source_url && (
                                    <div className='text-[#337AB7] text-xs mt-3 w-11/12 space-y-1'>
                                    {version.source_url
                                        .split(',')
                                        .map((url, index) => {
                                        const trimmedUrl = url.trim();
                                        return (
                                            <p key={index}>
                                            <a
                                                className='break-words overflow-hidden text-ellipsis'
                                                href={trimmedUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {trimmedUrl}
                                            </a>
                                            </p>
                                        );
                                        })}
                                    </div>
                                )}
                                </div>
                            );
                            })}
                        </div>
                        )}
                    </div>
                    );
                })}
                </div>
            ) : (
                <p>No metadata available.</p>
            )}
            </div>
        </div>
        </>,
        document.body
    );
};

export default MetricMetadataModal;